"use client"

import { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import Link from "next/link"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import { motion, useScroll, useTransform } from "motion/react"

import dynamic from "next/dynamic"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { TextMorph } from "@/components/ui/text-morph"

// LightRays est un effet purement décoratif (mix-blend-screen 30%)
// — chargé en lazy/no-SSR pour ne pas alourdir le bundle initial
// du Hero, mesuré responsable de ~300ms de Total Blocking Time
// sur Lighthouse mobile.
const LightRays = dynamic(
  () =>
    import("@/components/ui/light-rays").then((m) => ({
      default: m.LightRays,
    })),
  { ssr: false },
)

// Slide order matters: slide 0 is the LCP-anchoring image AND the
// first thing every visitor sees for ~6 s. We open with the
// highest-converting service for an industrial bois audience
// (affûtage), then the catalogue, then maintenance.
//
// CTA verbs were rewritten from the previous "Découvrir" / "En
// savoir plus" (epistemic, low-intent) to outcome verbs that match
// the buyer's actual goal — "Voir le service" / "Parcourir le
// catalogue" / "Programmer une visite". Per the CRO audit French B2B
// tools sites get measurable lifts from outcome-verb CTAs.
const slides = [
  {
    image: "/images/remote/saw-blade-1135923_1920.jpg",
    lines: [
      "AFFÛTAGE DE VOS OUTILS",
      "BOIS — DEPUIS 1986",
    ],
    href: "/affutage",
    label: "Voir le service affûtage",
  },
  {
    image: "/images/remote/conseil-outillage.jpg",
    lines: [
      "OUTILLAGE FESTOOL,",
      "CMT, MAKITA, LEMAN",
    ],
    href: "/outillage",
    label: "Parcourir le catalogue",
  },
  {
    image: "/images/remote/maintanance.jpg",
    lines: [
      "MAINTENANCE DE VOTRE",
      "PARC MACHINES À BOIS",
    ],
    href: "/maintenance",
    label: "Programmer une visite",
  },
]

const AUTOPLAY_MS = 6000
const ease = [0.16, 1, 0.3, 1] as const

export function Hero() {
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  const next = useCallback(
    () => setCurrent((c) => (c + 1) % slides.length),
    []
  )
  const prev = useCallback(
    () => setCurrent((c) => (c - 1 + slides.length) % slides.length),
    []
  )

  useEffect(() => {
    if (paused) return
    const id = setInterval(next, AUTOPLAY_MS)
    return () => clearInterval(id)
  }, [paused, next])

  // Parallax: background image moves slower than scroll
  const { scrollY } = useScroll()
  const bgY = useTransform(scrollY, [0, 600], [0, 80])

  return (
    <div>
      {/* ── Carousel with parallax ────────────────────────── */}
      <section
        className="relative mx-auto w-full max-w-7xl px-4 md:px-6"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") { e.preventDefault(); prev() }
          if (e.key === "ArrowRight") { e.preventDefault(); next() }
        }}
        tabIndex={0}
        aria-roledescription="carousel"
        aria-label="Diaporama principal"
      >
        {/* Status region for screen readers — annonce le slide courant
         *  quand l'auto-play change la diapositive (toutes les 6 s). On
         *  reste en aria-live="polite" pendant l'auto-play (annonce
         *  douce, sans interrompre la lecture en cours), et "off" quand
         *  l'utilisateur a la main (hover/focus → paused) pour ne pas
         *  doubler ses propres clicks. WCAG 4.1.3 (Status Messages, AA). */}
        <div
          role="status"
          aria-live={paused ? "off" : "polite"}
          aria-atomic="true"
          className="sr-only"
        >
          Diapositive {current + 1} sur {slides.length} :{" "}
          {slides[current].lines.join(" ")}
        </div>
        <div className="relative aspect-[16/7] md:aspect-[16/8] w-full overflow-hidden rounded-2xl md:rounded-3xl">
          {/* Slides with parallax */}
          {slides.map((slide, i) => (
            <div
              key={i}
              className={cn(
                "absolute inset-0 transition-[opacity] duration-700 ease-[var(--ease-premium)]",
                i === current ? "z-10 opacity-100" : "z-0 opacity-0"
              )}
              role="group"
              aria-roledescription="slide"
              aria-label={`Diapositive ${i + 1} sur ${slides.length}`}
              aria-hidden={i !== current ? true : undefined}
              inert={i !== current ? true : undefined}
            >
              {/*
               * Background image. Slide 0 (the LCP candidate) renders
               * WITHOUT the parallax `motion.div` wrapper so the
               * browser can paint it from raw HTML before the Hero's
               * client bundle hydrates. Subsequent slides keep the
               * `inset-[-20%]` + scroll-y parallax — those are below
               * the fold and never become LCP.
               *
               * Per the perf audit, dropping the wrapper on slide 0
               * shaves 0.8–1.2 s off mobile LCP because the image no
               * longer waits for `motion/react` (~30 KB gz) + the Hero
               * client bundle to boot.
               */}
              {i === 0 ? (
                <div className="absolute inset-0">
                  <Image
                    src={slide.image}
                    alt={slide.lines.join(" ")}
                    fill
                    className={cn(
                      "object-cover transition-transform duration-[6000ms] ease-linear",
                      i === current ? "scale-105" : "scale-100",
                    )}
                    sizes="(max-width: 768px) 100vw, 1280px"
                    priority
                    fetchPriority="high"
                  />
                </div>
              ) : (
                <motion.div className="absolute inset-[-20%]" style={{ y: bgY }} suppressHydrationWarning>
                  <Image
                    src={slide.image}
                    alt={slide.lines.join(" ")}
                    fill
                    className={cn(
                      "object-cover transition-transform duration-[6000ms] ease-linear",
                      i === current ? "scale-110" : "scale-100"
                    )}
                    sizes="(max-width: 768px) 100vw, 1280px"
                  />
                </motion.div>
              )}

              {/* Light rays effect */}
              <div className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none">
                <LightRays />
              </div>

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

            </div>
          ))}

          {/*
            Foreground text + CTA — rendus UNE SEULE FOIS hors de la
            boucle slides pour que <TextMorph> reçoive un même DOM
            persistant entre les changements de slide. Sans ça,
            chaque slide rendait son propre <TextMorph> et React les
            unmount/mount à chaque changement, ce qui empêchait la
            morphologie caractère-par-caractère (le composant
            n'observait qu'une seule valeur de children).
          */}
          <div className="pointer-events-none absolute inset-0 z-20 flex flex-col justify-center px-5 sm:px-10 md:px-16">
            {/* h2 with one TextMorph PER LINE — joining the lines into
                a single string broke the carousel : torph wraps each
                segment in inline-block, and an inline-block run of 38+
                characters (« OUTILLAGE FESTOOL, CMT, MAKITA, LEMAN »)
                stops obeying `text-wrap: balance` and gets clipped
                behind the right margin on mobile / md screens. Render
                each `slide.lines[n]` as its own block-level TextMorph
                so the layout flows line-by-line and the text always
                stays inside `max-w-xl`.

                Stagger between line 0 and line 1 = 40ms (dans la
                fenêtre 12-principles physics-no-excessive-stagger ≤
                50ms par item) pour un rythme orchestré subtle. */}
            <h2 className="pointer-events-auto max-w-2xl text-xl font-bold leading-[1.08] tracking-tight text-white sm:text-3xl md:text-5xl lg:text-6xl">
              {slides[current].lines.map((line, lineIndex) => (
                <TextMorph
                  key={lineIndex}
                  as="span"
                  className="block"
                  duration={600 + lineIndex * 40}
                  ease={{ stiffness: 200, damping: 22, mass: 0.9 }}
                >
                  {line}
                </TextMorph>
              ))}
            </h2>

            <div className="pointer-events-auto mt-5 sm:mt-6">
              <Button
                asChild
                size="default"
                className="group/slide-cta h-11 gap-2 rounded-full bg-primary px-5 text-primary-foreground active:scale-[0.97] transition-[transform] duration-200 ease-[var(--ease-out-expo)] sm:h-12 sm:px-6 sm:text-base"
              >
                <Link href={slides[current].href}>
                  {/* CTA label morph aussi entre slides — torph anime
                   *  quand le children change. */}
                  <TextMorph as="span" duration={500}>
                    {slides[current].label}
                  </TextMorph>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover/slide-cta:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          </div>

          {/*
            Navigation arrows — DESKTOP ONLY. On mobile the 44 px
            chevron buttons at left-3 / right-3 were landing right on
            top of the slide title ("LEMAN" in the screenshot got cut
            off by the right arrow). Hide them below `sm`; the dot
            indicators + horizontal-swipe handling below keep the
            carousel navigable on touch.
          */}
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-[background-color,transform] duration-200 ease-[var(--ease-out-expo)] hover:bg-white/40 active:scale-[0.97] sm:flex"
            aria-label="Diapositive précédente"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-[background-color,transform] duration-200 ease-[var(--ease-out-expo)] hover:bg-white/40 active:scale-[0.97] sm:flex"
            aria-label="Diapositive suivante"
          >
            <ChevronRight className="size-5" />
          </button>

          {/* Progress dot indicators — chaque bouton est un wrapper
              h-10 (40px) qui contient une dot visuelle au centre.
              Cela garantit un hit area WCAG 2.5.5 (24px min) +
              Lighthouse 'tap target size and spacing' sans superposer
              les boutons (le gap-1 + size-10 = 4px d'écart entre
              hit areas). */}
          {/* gap-0 entre les boutons + px-3 sur chaque bouton donnent
              24px de hit area horizontal (12px de padding × 2) en plus
              de la dot visible. Total : ~32px wide × 40px tall pour
              les inactive (axe target-size : min 24×24 OK), et avec
              gap-0 on évite que les hit areas se chevauchent puisque
              Lighthouse compare les bounding boxes. */}
          <div className="absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 items-center gap-0">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="group/dot grid h-10 cursor-pointer place-items-center px-3 transition-colors"
                aria-label={`Aller à la diapositive ${i + 1}`}
                aria-current={i === current ? "true" : undefined}
              >
                <span
                  className={cn(
                    "relative h-1 rounded-full overflow-hidden transition-[width,background-color] duration-300 ease-[var(--ease-premium)]",
                    i === current
                      ? "w-10 bg-white/30"
                      : "w-2 bg-white/30 group-hover/dot:bg-white/50"
                  )}
                  aria-hidden="true"
                >
                  {i === current && !paused && (
                    <motion.span
                      className="absolute inset-y-0 left-0 block rounded-full bg-white"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
                      key={`progress-${current}`}
                    />
                  )}
                  {i === current && paused && (
                    <span className="absolute inset-0 block rounded-full bg-white" />
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/*
        ── Mission text ───────────────────────────
        Mobile typography dialled down from the previous cascade
        (text-3xl headline, text-lg paragraphs). At ~360 px viewport
        those sizes produced ~12-line paragraphs that read as a wall.
        Smaller base step + tighter leading + a bit less vertical
        rhythm gives two ~6-line blocks that fit on one scroll view
        on a typical phone.
      */}
      <section className="mx-auto max-w-4xl pt-10 pb-8 px-5 sm:px-6 md:pt-20 md:pb-14">
        <motion.div
          className="space-y-4 text-center md:space-y-6"
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.12, delayChildren: 0.3 }}
        >
          <motion.h1
            className="text-2xl font-black uppercase tracking-tight text-primary sm:text-3xl md:text-5xl"
            variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.6, ease }}
          >
            ADIS 95
            <span
              className="mt-1.5 block text-[15px] font-semibold normal-case leading-snug tracking-normal text-foreground sm:text-lg md:text-xl md:mt-2"
              style={{ textWrap: "balance" }}
            >
              Affûtage, outillage et maintenance pour professionnels du bois en Île-de-France
            </span>
          </motion.h1>
          <motion.p
            className="mx-auto max-w-[62ch] text-[15px] leading-[1.6] text-muted-foreground sm:text-base sm:leading-relaxed md:text-xl"
            style={{ textWrap: "pretty" }}
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease }}
          >
            {"Depuis 1986, nous mettons notre expérience et notre savoir-faire à votre service pour l'affûtage de vos outils coupants, pour vous conseiller dans le choix de votre outillage pour vous accompagner et vous apporter toute notre expertise dans la maintenance de votre parc machines."}
          </motion.p>

          <motion.p
            className="mx-auto max-w-[62ch] text-[15px] font-bold leading-[1.55] text-foreground sm:text-base sm:leading-relaxed md:text-xl"
            style={{ textWrap: "pretty" }}
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease }}
          >
            {"Nous sommes à votre écoute pour tous vos besoins, dans le but de vous satisfaire avec la plus grande qualité de service, réactivité et compétitivité possible."}
          </motion.p>

          {/*
            CTAs: horizontal on desktop, stacked full-width pills on
            mobile. Previous `flex-wrap` let the two pills stack in
            natural content width, leaving a narrow "Nous contacter"
            chip floating alone on line 2. Full-width on mobile is
            the Apple / Luma convention and gives each CTA its own
            comfortable tap target.
          */}
          <motion.div
            className="flex flex-col items-stretch gap-2.5 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4 sm:pt-4"
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease }}
          >
            <Button
              size="lg"
              className="group/button h-12 gap-3 rounded-full pl-5 pr-2 active:scale-[0.97] transition-[transform] duration-200 ease-[var(--ease-out-expo)] sm:pl-6"
              asChild
            >
              <Link href="/affutage">
                <span>{"Découvrir nos services"}</span>
                <span
                  className={cn(
                    "flex size-8 items-center justify-center rounded-full",
                    "bg-primary-foreground/20 transition-colors",
                    "group-hover/button:bg-primary-foreground/30"
                  )}
                >
                  <ArrowRight className="size-4" />
                </span>
              </Link>
            </Button>

            <Button
              variant="ghost"
              size="lg"
              className="h-12 rounded-full border border-border px-6 active:scale-[0.97] transition-[transform] duration-200 ease-[var(--ease-out-expo)] sm:border-0"
              asChild
            >
              <Link href="/contact">Nous contacter</Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}
